import { inject, Injectable } from '@angular/core';
import { ApiService } from './api-service';
import { ImageAsset } from '../models/cms-models';

@Injectable({
  providedIn: 'root',
})
export class ImagesService {
  private apiService = inject(ApiService);
  private contentUrlCache = new Map<string, Promise<string>>();

  async getSiteImages(siteId: string): Promise<ImageAsset[]> {
    const images = await this.apiService.get<ImageAsset[]>(`api/image/site/${siteId}`);
    return Promise.all(images.map((image) => this.hydrateImage(image)));
  }

  async getImage(imageId: string): Promise<ImageAsset> {
    return this.hydrateImage(await this.apiService.get<ImageAsset>(`api/image/${imageId}`));
  }

  async createImage(input: {
    siteId: string;
    defaultAlt: string | null;
    defaultCaption: string | null;
    file: File;
  }): Promise<ImageAsset> {
    const formData = new FormData();
    formData.append('siteId', input.siteId);
    if (input.defaultAlt) {
      formData.append('defaultAlt', input.defaultAlt);
    }
    if (input.defaultCaption) {
      formData.append('defaultCaption', input.defaultCaption);
    }
    formData.append('file', input.file, input.file.name);
    const image = await this.apiService.post<ImageAsset, FormData>('api/image', formData);
    return this.hydrateImage(image);
  }

  async updateImage(imageId: string, input: {
    siteId: string;
    defaultAlt: string | null;
    defaultCaption: string | null;
  }): Promise<ImageAsset> {
    const image = await this.apiService.put<ImageAsset, typeof input>(`api/image/${imageId}`, input);
    return this.hydrateImage(image);
  }

  async deleteImage(imageId: string): Promise<void> {
    await this.apiService.delete(`api/image/${imageId}`);
  }

  async hydrateImage(image: ImageAsset): Promise<ImageAsset> {
    return {
      ...image,
      landingPageUsageCount: image.landingPageUsageCount ?? 0,
      galleryUsageCount: image.galleryUsageCount ?? 0,
      canDelete: image.canDelete ?? true,
      contentUrl: await this.resolveAuthorizedContentUrl(image.contentUrl),
    };
  }

  private async resolveAuthorizedContentUrl(contentUrl: string): Promise<string> {
    const resolvedUrl = this.apiService.resolveUrl(contentUrl);
    const existing = this.contentUrlCache.get(resolvedUrl);
    if (existing) {
      return existing;
    }

    const objectUrlPromise = this.apiService
      .getBlob(resolvedUrl)
      .then((blob) => URL.createObjectURL(blob));

    this.contentUrlCache.set(resolvedUrl, objectUrlPromise);
    return objectUrlPromise;
  }
}
